import { api } from './client';

export type OrganizationKind = 'LEGAL_ENTITY' | 'ENTREPRENEUR' | 'INDIVIDUAL' | 'FOREIGN';
export type TaxationSystem = 'OSNO' | 'USN_INCOME' | 'USN_INCOME_EXPENSE' | 'PSN' | 'ESHN' | 'AUSN';

export const ORGANIZATION_KIND_LABELS: Record<OrganizationKind, string> = {
  LEGAL_ENTITY: 'Юридическое лицо',
  ENTREPRENEUR: 'Индивидуальный предприниматель',
  INDIVIDUAL: 'Физическое лицо',
  FOREIGN: 'Иностранное лицо',
};

export const TAXATION_SYSTEM_LABELS: Record<TaxationSystem, string> = {
  OSNO: 'ОСНО',
  USN_INCOME: 'УСН «доходы»',
  USN_INCOME_EXPENSE: 'УСН «доходы минус расходы»',
  PSN: 'Патент',
  ESHN: 'ЕСХН',
  AUSN: 'АУСН',
};

export interface OrganizationBankAccount {
  id: string;
  organizationId: string;
  accountNumber: string;
  bankName: string;
  bik: string;
  corrAccount: string;
  bankCity: string | null;
  isDefault: boolean;
  isActive: boolean;
}

export interface Organization {
  id: string;
  shortName: string;
  fullName: string;
  kind: OrganizationKind;
  taxationSystem: TaxationSystem;
  inn: string | null;
  kpp: string | null;
  ogrn: string | null;
  ogrnipDate: string | null;
  okpo: string | null;
  addressFull: string | null;
  actualAddressFull: string | null;
  phone: string | null;
  email: string | null;
  directorPosition: string | null;
  directorName: string | null;
  accountantName: string | null;
  accountantIsDirector: boolean;
  edoParticipantId: string | null;
  isActive: boolean;
  logoDataUrl: string | null;
  bankAccounts: OrganizationBankAccount[];
}

export interface OrganizationInput {
  shortName: string;
  fullName: string;
  kind: OrganizationKind;
  taxationSystem: TaxationSystem;
  inn?: string | null;
  kpp?: string | null;
  ogrn?: string | null;
  ogrnipDate?: string | null;
  okpo?: string | null;
  addressFull?: string | null;
  actualAddressFull?: string | null;
  phone?: string | null;
  email?: string | null;
  directorPosition?: string | null;
  directorName?: string | null;
  accountantName?: string | null;
  accountantIsDirector?: boolean;
  edoParticipantId?: string | null;
  logoDataUrl?: string | null;
}

export interface BankAccountInput {
  accountNumber: string;
  bankName: string;
  bik: string;
  corrAccount: string;
  bankCity?: string | null;
  isDefault?: boolean;
  isActive?: boolean;
}

export interface OrganizationListResponse {
  items: Organization[];
  total: number;
  page: number;
  pageSize: number;
}

export interface OrganizationListParams {
  search?: string;
  includeInactive?: boolean;
  page?: number;
  pageSize?: number;
}

function buildQuery(params: OrganizationListParams): string {
  const search = new URLSearchParams();
  if (params.search) search.set('search', params.search);
  if (params.includeInactive) search.set('includeInactive', 'true');
  if (params.page !== undefined) search.set('page', String(params.page));
  if (params.pageSize !== undefined) search.set('pageSize', String(params.pageSize));
  const query = search.toString();
  return query ? `?${query}` : '';
}

export const organizationsApi = {
  list: (params: OrganizationListParams = {}) =>
    api.get<OrganizationListResponse>(`/organizations${buildQuery(params)}`),

  get: (id: string) => api.get<{ organization: Organization }>(`/organizations/${id}`),

  create: (data: OrganizationInput) =>
    api.post<{ organization: Organization }>('/organizations', data),

  update: (id: string, data: Partial<OrganizationInput>) =>
    api.patch<{ organization: Organization }>(`/organizations/${id}`, data),

  remove: (id: string) => api.delete<void>(`/organizations/${id}`),

  activate: (id: string) => api.post<{ organization: Organization }>(`/organizations/${id}/activate`),

  deactivate: (id: string) => api.post<{ organization: Organization }>(`/organizations/${id}/deactivate`),

  addBankAccount: (id: string, data: BankAccountInput) =>
    api.post<{ bankAccount: OrganizationBankAccount }>(`/organizations/${id}/bank-accounts`, data),

  updateBankAccount: (id: string, accountId: string, data: Partial<BankAccountInput>) =>
    api.patch<{ bankAccount: OrganizationBankAccount }>(
      `/organizations/${id}/bank-accounts/${accountId}`,
      data,
    ),

  deleteBankAccount: (id: string, accountId: string) =>
    api.delete<void>(`/organizations/${id}/bank-accounts/${accountId}`),
};
